import { defineField, defineType } from 'sanity'
import { richTextContent } from '../../data/richText'
import PromotionWheelFilters from '../../components/PromotionWheelFilters'
import VehicleInput from '../../components/VehicleInput'

const theme = defineField({
  title: 'Theme',
  name: 'theme',
  type: 'string',
  options: {
    list: [
      { title: 'Light', value: 'light' },
      { title: 'Medium', value: 'medium' },
      { title: 'Dark', value: 'dark' },
    ],
    layout: 'radio',
    direction: 'horizontal',
  },
  initialValue: 'light',
})

const disabled = defineField({
  title: 'Enabled',
  name: 'enabled',
  type: 'boolean',
  initialValue: true,
  description: 'If not enabled, it will not show',
})

export default defineType({
  title: 'Promotions',
  name: 'promotion',
  type: 'document',
  orderings: [
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
  ],
  fields: [
    defineField({
      title: 'Promotion Name',
      name: 'name',
      description: 'Used to identify this promo within CMS',
      type: 'string',
      group: 'content',
    }),
    defineField({
      title: 'Wheel Filters',
      name: 'wheelFilters',
      type: 'object',
      options: {
        collapsible: true,
        collapsed: true,
      },
      fields: [
        defineField({
          title: 'Construction Type',
          name: 'constructionType',
          type: 'string',
          initialValue: 'All',
        }),
        defineField({
          title: 'Designs',
          name: 'designs',
          type: 'array',
          of: [{ type: 'string' }],
          initialValue: [],
        }),
        defineField({
          title: 'Diameters',
          name: 'diameters',
          type: 'array',
          of: [{ type: 'string' }],
          initialValue: [],
        }),
        defineField({
          title: 'Finishes',
          name: 'finishes',
          type: 'array',
          of: [{ type: 'string' }],
          initialValue: [],
        }),
      ],
      initialValue: {
        constructionType: 'All',
        designs: [],
        diameters: [],
        finishes: [],
      },
      components: {
        input: PromotionWheelFilters,
      },
      group: 'content',
    }),
    defineField({
      name: 'showSaleFlag',
      title: 'Show sale flag',
      description: `By default 'sale' flag will be shown on product pages for design, finish, construction type fields. If you want to disable 'sale' label uncheck this option.`,
      type: 'boolean',
      initialValue: true,
      group: 'content',
    }),
    defineField({
      name: 'customSaleFlagText',
      title: 'Custom sale flag text',
      description: `If you want to change 'sale' flag text, enter custom text here.`,
      type: 'string',
      group: 'content',
      hidden: ({ parent }) => !parent?.showSaleFlag,
    }),
    defineField({
      title: 'Vehicle Filters',
      name: 'vehicleFilters',
      type: 'array',
      of: [
        {
          title: 'Vehicle',
          name: 'vehicle',
          type: 'object',
          fields: [
            defineField({
              title: 'Vehicle',
              name: 'vehicle',
              type: 'object',
              fields: [
                defineField({
                  name: 'make',
                  type: 'reference',
                  to: [{ type: 'make' }],
                }),
                defineField({
                  name: 'model',
                  type: 'reference',
                  to: [{ type: 'model' }],
                }),
                defineField({
                  name: 'generation',
                  type: 'reference',
                  to: [{ type: 'generation' }],
                }),
                defineField({
                  name: 'submodel',
                  type: 'reference',
                  to: [{ type: 'submodel' }],
                }),
              ],
              validation: (Rule) =>
                Rule.custom((vehicle) => {
                  if (!vehicle?.make) return 'At least Vehicle make is required'
                  return true
                }),
              components: {
                input: VehicleInput,
              },
            }),
          ],
          preview: {
            select: {
              make: 'vehicle.make.name',
              model: 'vehicle.model.name',
              generation: 'vehicle.generation.name',
              submodel: 'vehicle.submodel.name',
            },
            prepare({ make = '', model = '', generation = '', submodel = '' }) {
              const title = [make, model, generation, submodel]
                .filter((v) => v !== '' && v !== null && v !== undefined)
                .join(' ')
              return {
                title,
              }
            },
          },
        },
      ],
      group: 'content',
    }),
    defineField({
      title: 'Promotion Info',
      name: 'info',
      type: 'object',
      fields: [
        defineField({
          title: 'Start/End Dates',
          name: 'dates',
          type: 'object',
          fields: [
            defineField({
              title: 'Start Date/Time',
              name: 'start',
              type: 'datetime',
              options: {
                dateFormat: 'DD.MM.YYYY',
                timeFormat: 'HH:mm',
              },
              validation: (Rule) => Rule.required(),
            }),
            defineField({
              title: 'End Date/Time',
              name: 'end',
              type: 'datetime',
              options: {
                dateFormat: 'DD.MM.YYYY',
                timeFormat: 'HH:mm',
              },
              validation: (Rule) => Rule.required(),
            }),
          ],
          options: {
            collapsible: true,
            collapsed: true,
          },
        }),
        defineField({
          title: 'Configurator Banner',
          name: 'configuratorBanner',
          type: 'object',
          fields: [
            disabled,
            defineField({
              title: 'Title',
              name: 'title',
              type: 'string',
              validation: (Rule) => Rule.required(),
            }),
            defineField({
              title: 'Sub Title',
              name: 'subTitle',
              type: 'string',
              validation: (Rule) => Rule.required(),
            }),
            theme,
          ],
          options: {
            collapsible: true,
            collapsed: true,
          },
        }),
        defineField({
          title: 'Generation Banner',
          name: 'generationBanner',
          type: 'object',
          fields: [
            disabled,
            defineField({
              name: 'photo',
              title: 'Photo',
              type: 'image',
              options: {
                hotspot: true,
              },
            }),
          ],
          options: {
            collapsible: true,
            collapsed: true,
          },
        }),
        defineField({
          title: 'Promotion Page Info',
          name: 'promotionsPageInfo',
          type: 'object',
          fields: [
            disabled,
            defineField({
              name: 'photo',
              title: 'Photo',
              type: 'image',
              options: {
                hotspot: true,
              },
            }),
            defineField({
              name: 'description',
              title: 'Promo Description',
              type: 'array',
              of: richTextContent,
            }),
          ],
          options: {
            collapsible: true,
            collapsed: true,
          },
        }),
        defineField({
          title: 'Banner URL',
          name: 'url',
          type: 'link',
          description: 'This only applies to Generations & Promotion Pages',
          validation: (Rule) => Rule.required(),
          options: {
            collapsible: true,
            collapsed: true,
          },
        }),
        defineField({
          title: 'Details Modal',
          name: 'modal',
          type: 'promotionModal',
          validation: (Rule) => Rule.required(),
          options: {
            collapsible: true,
            collapsed: true,
          },
        }),
      ],
      group: 'content',
    }),
  ],
  preview: {
    select: {
      title: 'name',
    },
  },
})